import React, {useCallback, useContext, useEffect, useState} from 'react';

import {NumberInput, NumberInputTypes} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark/required-mark';

import Button from '@pb/kit-component-button';
import CheckBox from '@pb/kit-component-checkbox';
import {Float, Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import SlideBox, {SlideBoxButton} from '@pb/kit-component-slide-box';
import {Textarea} from '@pb/kit-component-textarea-v2';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';
import {stringToNumber} from '@pb/kit-formatters';

import {MAX_CONTRACT_FIELD_VALUE} from '../../../constants';

import {LocaleNumberInput} from './locale-number-input';

import {
    mcpContractChange,
    mcpContractChangeForm,
    mcpContractChangeFormContent,
    mcpContractChangeHandle,
} from './change-credits-form.module.styl';

import type {McpUpdateCreditsRequest} from 'src/openapi/admin/models/McpUpdateCreditsRequest';


type Props = {
    costPerCredit: number,
    costPerOverageCredit: number,
    remainingOverageCredits: number,
    onApplyCredits: (data: McpUpdateCreditsRequest) => void,
};

export const ChangeCreditForm = ({
    costPerCredit, costPerOverageCredit, remainingOverageCredits, onApplyCredits,
}: Props) => {
    const {locale} = useContext(LocaleContext);
    const [formExpanded, setFormExpanded] = useState(false);
    const [creditsAmount, setCreditsAmount] = useState('');
    const [costPerCreditInput, setCostPerCreditInput] = useState<number | undefined>(costPerCredit);
    const [overageCreditsAmount, setOverageCreditsAmount] = useState<number | undefined>(
        remainingOverageCredits,
    );
    const [costPerOverageCreditInput, setCostPerOverageCreditInput] = useState<number | undefined>(
        costPerOverageCredit,
    );
    const [comment, setComment] = useState('');

    useEffect(() => {
        setCostPerCreditInput(costPerCredit);
    }, [costPerCredit]);

    useEffect(() => {
        setCostPerOverageCreditInput(costPerOverageCredit);
    }, [costPerOverageCredit]);

    useEffect(() => {
        setOverageCreditsAmount(remainingOverageCredits);
    }, [remainingOverageCredits]);

    const onToggleForm = useCallback(() => {
        setFormExpanded(prev => !prev);
    }, []);

    const onRestoreForm = useCallback(() => {
        setCreditsAmount('');
        setCostPerCreditInput(costPerCredit);
        setOverageCreditsAmount(remainingOverageCredits);
        setCostPerOverageCreditInput(costPerOverageCredit);
        setComment('');
    }, [costPerCredit, costPerOverageCredit, remainingOverageCredits]);

    const isAnyFieldChanged = Boolean(creditsAmount)
        || overageCreditsAmount !== remainingOverageCredits
        || costPerCreditInput !== costPerCredit
        || costPerOverageCreditInput !== costPerOverageCredit;

    const canApply = isAnyFieldChanged && comment.trim().length > 0;

    const onApply = useCallback(() => {
        const credits = stringToNumber(creditsAmount, locale);
        onApplyCredits({
            credits: credits || undefined,
            costPerCredit: costPerCreditInput,
            overageCredits: overageCreditsAmount != null
                ? overageCreditsAmount - remainingOverageCredits
                : undefined,
            costPerOverageCredit: costPerOverageCreditInput,
            comment: comment || undefined,
        });
        onRestoreForm();
    }, [creditsAmount, overageCreditsAmount, remainingOverageCredits, locale,
        costPerCreditInput, costPerOverageCreditInput, comment, onApplyCredits, onRestoreForm]);

    return (
        <div className={mcpContractChange}>
            <div className={mcpContractChangeHandle}>
                <SlideBoxButton onClick={onToggleForm}>
                    {slideBoxClick => (
                        <CheckBox
                            name="default"
                            onCheckBoxChange={slideBoxClick}
                            checked={formExpanded}
                            custom={true}
                            usePropsOnly={true}
                        />
                    )}
                </SlideBoxButton>
            </div>

            <div className={mcpContractChangeForm}>
                <Typography fontWeight="semi-bold" variant="heading-level-5">Change Credits</Typography>

                <SlideBox shown={formExpanded}>
                    <div className={mcpContractChangeFormContent}>
                        <Group direction="v">
                            <Group alignItems="start">
                                <Group direction="v" gap="s" style={{flex: 1}}>
                                    <Item>
                                        <NumberInput
                                            inputType={NumberInputTypes.INTEGER}
                                            label="Number of Credits"
                                            maxValue={MAX_CONTRACT_FIELD_VALUE}
                                            value={creditsAmount ? +creditsAmount : undefined}
                                            displayedValue={creditsAmount ? Integer.format(+creditsAmount, locale) : ''}
                                            onBlur={value => setCreditsAmount(value ? value.toString() : '')}
                                        />
                                    </Item>
                                    <Item>
                                        <NumberInput
                                            inputType={NumberInputTypes.FLOAT}
                                            label="Credit Cost, $"
                                            placeholder="$, USD"
                                            maxValue={MAX_CONTRACT_FIELD_VALUE}
                                            value={costPerCreditInput}
                                            displayedValue={costPerCreditInput != null ? Float.format(costPerCreditInput, locale, {hideTrailingZeros: true}) : ''}
                                            onBlur={value => setCostPerCreditInput(value != null ? +value : undefined)}
                                        />
                                    </Item>
                                </Group>
                                <Group direction="v" gap="s" style={{flex: 1}}>
                                    <Item>
                                        <LocaleNumberInput
                                            label="Overages"
                                            value={overageCreditsAmount}
                                            maxValue={MAX_CONTRACT_FIELD_VALUE}
                                            onChange={setOverageCreditsAmount}
                                        />
                                    </Item>
                                    <Item>
                                        <NumberInput
                                            inputType={NumberInputTypes.FLOAT}
                                            label="Overage Cost, $"
                                            placeholder="$, USD"
                                            maxValue={MAX_CONTRACT_FIELD_VALUE}
                                            value={costPerOverageCreditInput}
                                            displayedValue={costPerOverageCreditInput != null
                                                ? Float.format(
                                                        costPerOverageCreditInput, locale, {hideTrailingZeros: true},
                                                    )
                                                : ''}
                                            onBlur={value => setCostPerOverageCreditInput(
                                                value != null ? +value : undefined,
                                            )}
                                        />
                                    </Item>
                                </Group>
                            </Group>
                            <Item>
                                <Label><RequiredMark label="Comment"/></Label>
                                <Textarea
                                    value={comment}
                                    resizable={true}
                                    style={{height: 80}}
                                    maxLength={255}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                        setComment(e.target.value)}
                                />
                            </Item>
                            <Group justify="end">
                                <Button
                                    variant="Type.PRIMARY"
                                    disabled={!canApply}
                                    onClick={canApply ? onApply : undefined}>
                                    Apply
                                </Button>
                                <Button
                                    variant="Type.SECONDARY"
                                    disabled={!isAnyFieldChanged && !comment}
                                    onClick={onRestoreForm}>
                                    Cancel
                                </Button>
                            </Group>
                        </Group>
                    </div>
                </SlideBox>
            </div>
        </div>
    );
};
